import React from 'react';
import { useGameCardGuideStore } from 'lib/store/gameCardGuideStore';
import { fetchGameCardPlaceOrder } from 'lib/apis';
import { report } from 'lib/utils/dataReport';
import { ATTA_REPORT } from 'lib/constants/atta';
import { closeModal } from 'lib/components/Modal';
import { showThirdPartyLogin } from 'lib/components/ThirdPartyLogin';
import { ENV_TYPE } from 'lib/constants/login';
import { getLoginType } from 'lib/utils/util';
import storage from 'lib/utils/storage';
import toast from 'lib/utils/toast';

const PENDING_GUIDE_NAVIGATION_KEY = 'gameCardGuidePendingNavigation';
const PENDING_GUIDE_NAVIGATION_EXPIRE_MS = 5 * 60 * 1000;
const PLACE_ORDER_ERROR_MSG = '下单失败，请稍后再试';

const GUIDE_ELIGIBLE_REASONS = new Set([
  201013, // 资格限制 — 您没有参与活动的资格
  201014, // 地区限制 — 您的手机号所属地区不支持订购
  201015, // 余额不足 — 您的手机余额不足
  201016, // 号码异常 — 您的手机号状态异常（无安慰礼包）
  201017, // 号段不支持 — 您的手机号不支持订购
  201018, // 月度限制 — 您本月没有参与活动的资格
  201019, // 余额/状态异常 — 您的手机余额不足或者状态异常
  201021, // 携号转网 — 携号转网用户不允许订购
  201022, // 资格限制 — 您没有参与活动的资格
  201023, // 省份限制 — 您的手机号所属的省份不支持订购
  201028, // 通用不支持 — 您当前手机号暂不支持订购
  201029, // 资格限制 — 您没有参与活动的资格
]);

type GuideExtraParams = {
  unsupportedReason?: number;
};

class GameCardGuideService {
  public isGuideAvailable(): boolean {
    return useGameCardGuideStore.getState().isGuideAvailable();
  }

  /**
   * 判断当前 unsupportedReason 是否应展示引导王卡
   * 可订购列表为空（unsupportedReason 为空）时也展示引导
   */
  public isEligibleForGuide(unsupportedReason?: number): boolean {
    if (!this.isGuideAvailable()) return false;
    const reason = unsupportedReason ?? useGameCardGuideStore.getState().unsupportedReason;
    if (reason === undefined || reason === null) return true;
    return GUIDE_ELIGIBLE_REASONS.has(reason);
  }

  public async navigateGuideUrl() {
    const url = this.getValidGuideUrl();
    if (!url) return;

    const { isOrderRequired } = useGameCardGuideStore.getState();
    if (!isOrderRequired) {
      window.location.href = url.href;
      return;
    }

    if (getLoginType() === ENV_TYPE.GUEST) {
      this.recordPendingGuideNavigation();
      showThirdPartyLogin({});
      return;
    }

    const orderId = await this.fetchGameCardOrderId();
    if (!orderId) {
      toast.info(PLACE_ORDER_ERROR_MSG);
      return;
    }

    url.searchParams.set('orderid', orderId);
    window.location.href = url.href;
  }

  public resumePendingGuideNavigation() {
    const pending = storage.getStorage(PENDING_GUIDE_NAVIGATION_KEY);
    if (!pending) return;

    const { isOrderRequired } = useGameCardGuideStore.getState();
    if (!this.isGuideAvailable() || !isOrderRequired) {
      this.clearPendingGuideNavigation();
      return;
    }

    if (getLoginType() === ENV_TYPE.GUEST) return;

    this.clearPendingGuideNavigation();
    this.navigateGuideUrl();
  }

  public buildGuideFooter(source: string, extraParams?: GuideExtraParams): React.ReactNode {
    if (!this.isEligibleForGuide(extraParams?.unsupportedReason)) return undefined;
    const store = useGameCardGuideStore.getState();
    const onClick = () => {
      closeModal();
      this.reportClick(source);
      this.navigateGuideUrl();
    };
    return React.createElement('div', { onClick, style: { cursor: 'pointer', textAlign: 'center' } }, store.guideText);
  }

  public getNoGiftGuideConfig(source: string, extraParams?: GuideExtraParams) {
    if (!this.isEligibleForGuide(extraParams?.unsupportedReason)) return undefined;
    const store = useGameCardGuideStore.getState();
    return {
      confirmText: store.guideIcon
        ? React.createElement(
            React.Fragment,
            null,
            React.createElement('img', {
              src: store.guideIcon,
              alt: '',
              style: { height: '1em', verticalAlign: 'middle', marginRight: 4 },
            }),
            store.noGiftButtonText,
          )
        : store.noGiftButtonText,
      confirmStyle: {
        fontSize: '16px',
      },
      confirmCallback: () => {
        this.reportClick(source);
        this.navigateGuideUrl();
      },
      footer: this.buildGuideFooter(source, extraParams),
    };
  }

  private getValidGuideUrl(): URL | undefined {
    const { guideUrl } = useGameCardGuideStore.getState();
    try {
      const url = new URL(guideUrl);
      if (url.protocol !== 'https:' && url.protocol !== 'http:') {
        toast.info('页面暂不可用，请稍后再试');
        return;
      }
      return url;
    } catch {
      toast.info('页面暂不可用，请稍后再试');
      return;
    }
  }

  private recordPendingGuideNavigation() {
    storage.setStorage(PENDING_GUIDE_NAVIGATION_KEY, true, Date.now() + PENDING_GUIDE_NAVIGATION_EXPIRE_MS);
  }

  private clearPendingGuideNavigation() {
    storage.removeStorage(PENDING_GUIDE_NAVIGATION_KEY);
  }

  private async fetchGameCardOrderId(): Promise<string> {
    try {
      const res = await fetchGameCardPlaceOrder();
      if (res?.code !== 0) return '';
      if (typeof res?.data !== 'string') return '';
      const orderId = res.data.trim();
      if (!orderId) return '';
      return orderId;
    } catch {
      return '';
    }
  }

  private reportClick(source: string) {
    report(`${source}_${ATTA_REPORT.GAME_CARD_GUIDE.CLICK}`);
  }
}

const gameCardGuideService = new GameCardGuideService();
export default gameCardGuideService;
